import React, { useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Select as AdminSelect,
  Skeleton,
  Table,
  useConfirm,
  useToast,
} from "stories/Admin";
import styles from "./SuperAdminPrayerTimesPage.module.scss";
import Select, { createFilter } from "react-select";
import useFileFetch from "hooks/useFileFetch";
import { adminSelectStyles } from "../UsersPage/adminSelectStyles";
import PageModal from "../LocationsPage/PageModal";

interface Location {
  id: number;
  name: string;
  prayerTimesTables: { id: number; name: string }[];
}

interface PrayerTimes {
  date: string;
  fajr: string;
  fajr_endtime: string;
  duhr: string;
  asr: string;
  maghrib: string;
  isha: string;
  asr_1x_shadow: string;
  asr_2x_shadow: string;
}

const SuperAdminPrayerTimesPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [prayerTimesTables, setPrayerTimesTables] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [prayerTimesData, setPrayerTimesData] = useState<PrayerTimes[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalLocationId, setModalLocationId] = useState<number | null>(null);
  const [modalPrayerTimesTables, setModalPrayerTimesTables] = useState<
    { id: number; name: string }[]
  >([]);
  const [modalTableId, setModalTableId] = useState<number | null>(null);
  const [modalYear, setModalYear] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const { execute, isLoading } = useFetch();
  const { executeFileFetch } = useFileFetch();

  const customFilter = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const result = await execute(`/api/v1/locations`, "GET");
        if (result) {
          const sortedLocations = result.sort((a: Location, b: Location) =>
            a.name.localeCompare(b.name),
          );
          setLocations(sortedLocations);
        }
      } catch (err) {
        setError("Kunne ikke hente lokasjoner.");
      }
    };
    fetchLocations();
  }, []);

  const fetchPrayerTimes = async () => {
    if (!selectedLocationId || !selectedTableId || selectedYear === null)
      return;

    const endpoint = `/api/v1/prayerTimes/${selectedLocationId}/${selectedTableId}/${selectedYear}`;

    try {
      const result = await execute(endpoint, "GET");
      if (!result || result.length === 0) {
        setPrayerTimesData([]);
        setError("Ingen bønnetider funnet for det valgte året.");
      } else {
        setPrayerTimesData(result);
        setError(null);
      }
    } catch (err) {
      setPrayerTimesData([]);
      setError(
        "Kunne ikke hente bønnetider for den valgte lokasjonen og tabellen.",
      );
    }
  };

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const locationId = parseInt(event.target.value);
    const selectedLocation = locations.find((loc) => loc.id === locationId);

    setSelectedLocationId(locationId);
    setPrayerTimesTables(selectedLocation?.prayerTimesTables || []);
    setSelectedTableId(null);
    setSelectedYear(null);
    setPrayerTimesData([]);
    setError(null);
  };

  const handleTableChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTableId(parseInt(event.target.value));
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(event.target.value));
  };

  useEffect(() => {
    if (selectedLocationId && selectedTableId && selectedYear !== null) {
      fetchPrayerTimes();
    }
  }, [selectedLocationId, selectedTableId, selectedYear]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setUploadStatus(null);
    }
  };

  const handleFileUpload = async () => {
    if (!csvFile || !modalLocationId || !modalTableId || !modalYear) {
      setUploadStatus(
        "Vennligst sørg for at alle felt er valgt og at en fil er valgt.",
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", csvFile);

    try {
      const uploadUrl = `/api/v1/prayerTimes/${modalLocationId}/${modalTableId}/${modalYear}/upload-csv`;
      await execute(uploadUrl, "POST", formData);
      setUploadStatus("Filen ble lastet opp!");
      console.log("Filen ble lastet opp!");
      fetchPrayerTimes();
      setCsvFile(null);
    } catch (error) {
      setUploadStatus("Kunne ikke laste opp filen.");
      console.error("Kunne ikke laste opp filen.", error);
    }
  };

  const handleDeletePrayerTimes = async () => {
    if (!selectedLocationId || !selectedTableId) {
      toast.error("Velg lokasjon og tabell før du sletter bønnetider.");
      return;
    }

    const ok = await confirm({
      title: "Slett bønnetider?",
      message:
        "Bønnetidene for valgt lokasjon og tabell slettes. Dette kan ikke angres.",
      confirmLabel: "Slett bønnetider",
      tone: "danger",
    });
    if (!ok) return;

    try {
      const deleteUrl = `/api/v1/prayerTimes/${selectedLocationId}/${selectedTableId}`;
      await execute(deleteUrl, "DELETE");
      toast.success("Bønnetidene er slettet");
      setPrayerTimesData([]);
      fetchPrayerTimes();
    } catch (error) {
      console.error("Feil ved sletting av bønnetider:", error);
      toast.error("Kunne ikke slette bønnetidene. Prøv igjen.");
    }
  };

  const resetModalStates = () => {
    setModalLocationId(null);
    setModalPrayerTimesTables([]);
    setModalTableId(null);
    setModalYear(null);
    setCsvFile(null);
    setUploadStatus(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCsvDownload = async () => {
    if (!selectedLocationId || !selectedTableId || !selectedYear) {
      toast.error("Velg lokasjon, bønnetabell og år før eksport.");
      return;
    }

    try {
      const blob = await executeFileFetch(
        `/api/v1/prayerTimes/${selectedLocationId}/${selectedTableId}/${selectedYear}/download-csv`,
        "GET",
      );

      if (!blob) {
        throw new Error("Kunne ikke laste ned CSV-filen.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `prayer_times_${selectedLocationId}_${selectedTableId}_${selectedYear}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Feil ved nedlasting av CSV:", error);
      toast.error("Noe gikk galt ved nedlasting av CSV.");
    }
  };

  const formatCsvData = (data: PrayerTimes[]) => {
    const headers = [
      { label: "Dato", key: "date" },
      { label: "Fajr", key: "fajr" },
      { label: "Sunrise", key: "fajr_endtime" },
      { label: "Duhr", key: "duhr" },
      { label: "Asr", key: "asr" },
      { label: "Asr1x", key: "asr_1x_shadow" },
      { label: "Asr2x", key: "asr_2x_shadow" },
      { label: "Maghrib", key: "maghrib" },
      { label: "Isha", key: "isha" },
    ];

    const rows = data.map((row) => ({
      date: new Intl.DateTimeFormat("default", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(row.date)),
      fajr: row.fajr,
      fajr_endtime: row.fajr_endtime,
      duhr: row.duhr,
      asr: row.asr,
      asr_1x_shadow: row.asr_1x_shadow,
      asr_2x_shadow: row.asr_2x_shadow,
      maghrib: row.maghrib,
      isha: row.isha,
    }));

    return [headers.map((header) => header.label), ...rows.map(Object.values)];
  };

  const years = Array.from(
    { length: 7 },
    (_, index) => new Date().getFullYear() + index,
  );

  const locationOptions = locations.map((location) => ({
    value: location.id,
    label: location.name,
  }));

  const selectedLocation = locations.find(
    (loc) => loc.id === selectedLocationId,
  );
  const selectedTable = prayerTimesTables.find((t) => t.id === selectedTableId);

  const formatDay = (date: string) =>
    new Intl.DateTimeFormat("nb-NO", {
      day: "numeric",
      month: "short",
    })
      .format(new Date(date))
      .replace(/\.$/, "");

  const columns: Array<Column<PrayerTimes>> = [
    { key: "date", header: "Dato", render: (t) => <b>{formatDay(t.date)}</b> },
    { key: "fajr", header: "Fajr", numeric: true, render: (t) => t.fajr },
    {
      key: "sunrise",
      header: "Soloppgang",
      numeric: true,
      render: (t) => t.fajr_endtime,
    },
    { key: "duhr", header: "Dhuhr", numeric: true, render: (t) => t.duhr },
    { key: "asr", header: "Asr", numeric: true, render: (t) => t.asr },
    {
      key: "asr1",
      header: "Asr 1x",
      numeric: true,
      render: (t) => t.asr_1x_shadow,
    },
    {
      key: "asr2",
      header: "Asr 2x",
      numeric: true,
      render: (t) => t.asr_2x_shadow,
    },
    {
      key: "maghrib",
      header: "Maghrib",
      numeric: true,
      render: (t) => t.maghrib,
    },
    { key: "isha", header: "Isha", numeric: true, render: (t) => t.isha },
  ];

  const hasData =
    !!selectedLocationId &&
    !!selectedTableId &&
    !!selectedYear &&
    prayerTimesData.length > 0;

  const closeModal = () => {
    setModalVisible(false);
    resetModalStates();
  };

  const formatSize = (bytes: number) =>
    bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} kB`;

  return (
    <>
      <PageHeader
        title="Bønnetider"
        subtitle="Se, last opp og slett bønnetider per lokasjon, tabell og år."
        actions={
          <Button
            icon={<Icon name="upload" size={16} />}
            onClick={() => setModalVisible(true)}
          >
            Last opp CSV
          </Button>
        }
      />

      <Card>
        <div className={styles.filters}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="locationSelect">
              Lokasjon
            </label>
            <Select
              inputId="locationSelect"
              options={locationOptions}
              onChange={(selectedOption: { value: number } | null) => {
                const locationId = selectedOption?.value || null;
                const selectedLocation = locations.find(
                  (loc) => loc.id === locationId,
                );

                setSelectedLocationId(locationId);
                setPrayerTimesTables(selectedLocation?.prayerTimesTables || []);
                setSelectedTableId(null);
                setSelectedYear(null);
                setPrayerTimesData([]);
                setError(null);
              }}
              placeholder="Søk og velg lokasjon"
              filterOption={customFilter}
              styles={adminSelectStyles<{ value: number; label: string }>()}
              noOptionsMessage={() => "Ingen lokasjoner funnet"}
            />
          </div>
          <AdminSelect
            id="tableSelect"
            label="Bønnetabell"
            value={selectedTableId || ""}
            onChange={handleTableChange}
            disabled={prayerTimesTables.length === 0}
          >
            <option value="" disabled>
              Velg en bønnetabell
            </option>
            {prayerTimesTables.map((table) => (
              <option key={table.id} value={table.id}>
                {table.name} (#{table.id})
              </option>
            ))}
          </AdminSelect>
          <AdminSelect
            id="yearSelect"
            label="År"
            value={selectedYear || ""}
            onChange={handleYearChange}
            disabled={!(selectedLocationId && selectedTableId)}
          >
            <option value="" disabled>
              Velg et år
            </option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </AdminSelect>
        </div>
        {selectedLocationId && prayerTimesTables.length === 0 && (
          <Alert tone="warn">
            Ingen bønnetabeller tilgjengelig for denne lokasjonen.
          </Alert>
        )}
      </Card>

      {isLoading ? (
        <Card>
          <div className={styles.skeletons}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} height={32} />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Alert tone="error">{error}</Alert>
      ) : (
        selectedYear !== null && (
          <Card
            title={[selectedLocation?.name, selectedTable?.name, selectedYear]
              .filter(Boolean)
              .join(" · ")}
            actions={
              hasData && (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Icon name="download" size={14} />}
                    onClick={handleCsvDownload}
                  >
                    Eksporter CSV
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={<Icon name="trash" size={14} />}
                    onClick={handleDeletePrayerTimes}
                  >
                    Slett bønnetider
                  </Button>
                </>
              )
            }
          >
            <Table
              columns={columns}
              rows={prayerTimesData}
              rowKey={(t) => t.date}
              compact
              empty={
                <EmptyState icon="clock" title="Ingen bønnetider">
                  Last opp en CSV-fil for dette året.
                </EmptyState>
              }
            />
          </Card>
        )
      )}

      <PageModal
        open={modalVisible}
        title="Last opp bønnetider"
        icon="upload"
        width={640}
        onClose={closeModal}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Lukk
            </Button>
            <Button
              icon={<Icon name="upload" size={16} />}
              onClick={handleFileUpload}
              disabled={
                !csvFile || !modalLocationId || !modalTableId || !modalYear
              }
            >
              Last opp
            </Button>
          </>
        }
      >
        <div className={styles.filters}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="modalLocationSelect">
              Lokasjon
            </label>
            <Select
              inputId="modalLocationSelect"
              options={locations
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((location) => ({
                  value: location.id,
                  label: location.name,
                }))}
              value={
                modalLocationId
                  ? {
                      value: modalLocationId,
                      label:
                        locations.find((loc) => loc.id === modalLocationId)
                          ?.name ?? "",
                    }
                  : null
              }
              onChange={(selectedOption) => {
                const locationId = selectedOption?.value || null;
                const selectedLocation = locations.find(
                  (loc) => loc.id === locationId,
                );

                setModalLocationId(locationId);
                setModalPrayerTimesTables(
                  selectedLocation?.prayerTimesTables || [],
                );
                setModalTableId(null);
                setModalYear(null);
              }}
              placeholder="Søk og velg lokasjon"
              isClearable
              filterOption={createFilter({
                ignoreCase: true,
                ignoreAccents: true,
                matchFrom: "any",
                stringify: (option) => option.label,
              })}
              styles={adminSelectStyles<{ value: number; label: string }>()}
              menuPortalTarget={document.body}
              noOptionsMessage={() => "Ingen lokasjoner funnet"}
            />
          </div>
          <AdminSelect
            label="Bønnetabell"
            value={modalTableId || ""}
            onChange={(event) => setModalTableId(parseInt(event.target.value))}
            disabled={!modalPrayerTimesTables.length}
          >
            <option value="" disabled>
              Velg en bønnetabell
            </option>
            {modalPrayerTimesTables.map((table) => (
              <option key={table.id} value={table.id}>
                {table.name} (#{table.id})
              </option>
            ))}
          </AdminSelect>
          <AdminSelect
            label="År"
            value={modalYear || ""}
            onChange={(event) => setModalYear(parseInt(event.target.value))}
            disabled={!modalTableId}
          >
            <option value="" disabled>
              Velg et år
            </option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </AdminSelect>
        </div>

        <label className={styles.drop}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className={styles.fileInput}
            onChange={handleFileSelect}
          />
          <span className={styles.dropIcon} aria-hidden="true">
            <Icon name="doc" size={22} />
          </span>
          <span className={styles.dropText}>
            {csvFile ? (
              <>
                <b>{csvFile.name}</b>
                <span className={styles.muted}>{formatSize(csvFile.size)}</span>
              </>
            ) : (
              <>
                <b>Velg CSV-fil</b>
                <span className={styles.muted}>
                  Klikk for å velge en .csv-fil fra maskinen din.
                </span>
              </>
            )}
          </span>
          {csvFile && (
            <span className={styles.dropRemove}>
              <IconButton
                label="Fjern fil"
                size="sm"
                icon={<Icon name="x" size={16} />}
                onClick={(e) => {
                  e.preventDefault();
                  setCsvFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              />
            </span>
          )}
        </label>

        {uploadStatus && (
          <Alert tone={uploadStatus.includes("lastet opp") ? "ok" : "error"}>
            {uploadStatus}
          </Alert>
        )}
      </PageModal>
    </>
  );
};

export default SuperAdminPrayerTimesPage;
